import type { IUserInfo } from "@/hooks/use-user-info";
import { BENEFIT_TYPE, BILLING_CLASS, matchPosItemType } from "@/lib/cost-util";
import type { PlaceOfService, ProviderCost } from "@/model/provider-cost";
import { ProviderCostView } from "@/model/provider-cost";
import type { SpendSummaryAccumulatorWithTier } from "@/model/spend-summary";
import type { BenefitBreakdownResults, Service } from "@/types";
import type { UseQueryResult } from "@tanstack/react-query";

type PosLevelCostBreakdownProps = {
  posItem: PlaceOfService;
  providerCost: ProviderCost;
  userInfo: IUserInfo;
  benefitBreakdownQueryData: UseQueryResult<BenefitBreakdownResults, Error>;
  spendSummaryAccData: SpendSummaryAccumulatorWithTier[];
};

const PosLevelCostBreakdown = ({
  posItem,
  providerCost,
  userInfo,
  benefitBreakdownQueryData,
  spendSummaryAccData,
}: PosLevelCostBreakdownProps) => {
  const providerCostViewModelData = new ProviderCostView(providerCost);
  const posItemResult = providerCostViewModelData?.placeOfServiceRates(
    posItem?.services,
  );
  const posItemCode = matchPosItemType.find(
    (item) => item?.name === posItem?.type,
  )?.code;

  // console.log("posItemResult ***", posItemResult);

  //used values
  const totalRate = posItemResult?.estimatedTotal?.rate ?? "N/A";
  const professionalRate = posItemResult?.professional?.rate ?? "N/A";
  const institutionalRate = posItemResult?.facility?.rate ?? "N/A";

  function getContractedRate(billingClass: BILLING_CLASS) {
    const isPercentageType =
      posItemResult?.[billingClass.toLowerCase()].negotiatedRate.label.includes(
        "percentage",
      );
    // console.log("nego% ***", isPercentageType);
    let minContractedRate =
      posItemResult?.[billingClass.toLowerCase()].negotiatedRate.min;
    let maxContractedRate =
      posItemResult?.[billingClass.toLowerCase()].negotiatedRate.max;

    if (!minContractedRate || !isFinite(minContractedRate))
      minContractedRate = "N/A";
    if (!maxContractedRate || !isFinite(maxContractedRate))
      maxContractedRate = "N/A";

    if (isPercentageType && minContractedRate !== "N/A") {
      minContractedRate = `${minContractedRate}%`;
    }
    if (isPercentageType && maxContractedRate !== "N/A") {
      maxContractedRate = `${maxContractedRate}%`;
    }

    return {
      min:
        typeof minContractedRate === "number"
          ? `$${minContractedRate}`
          : minContractedRate,
      max:
        typeof maxContractedRate === "number"
          ? `$${maxContractedRate}`
          : maxContractedRate,
    };
  }

  function showContractedRateRange(billingClass: BILLING_CLASS) {
    const { min, max } = getContractedRate(billingClass);
    return min === max ? min : `${min} - ${max}`;
  }

  function getOopDed() {
    const spendSummaryData = spendSummaryAccData.find(
      (member) => member?.memberId === userInfo?.memberId,
    );
    return spendSummaryData;
  }

  function showOopDed(type: "OOP" | "DED") {
    if (type === "OOP")
      return `$${getOopDed()?.OOPPaid} of $${getOopDed()?.OOPMax} OOP
    Max`;
    if (type === "DED")
      return `$${getOopDed()?.deductiblePaid} of $${getOopDed()?.deductibleLimit} Deductible Limit`;
  }

  function getCopayCoinsurance(billingClass: BILLING_CLASS) {
    const posItemData = benefitBreakdownQueryData.data?.results
      ?.find((item) => item?.categoryCode === providerCost?.specialityCode)
      ?.data.find((item) => item?.tier === providerCost?.networkTier)
      ?.placeOfService.filter(
        (billingClassItem) =>
          billingClassItem?.billingClass.toUpperCase() === billingClass,
      )
      .map((billingClassItem) =>
        billingClassItem?.services.filter(
          (service) => service?.code === posItemCode,
        ),
      )[0][0];
    if (!posItemData)
      return {
        code: "N/A",
        codeDescription: "N/A",
        copay: "N/A",
        coinsurance: "N/A",
      };
    return posItemData;
  }

  function showCopayCoinsuranceRange(
    billingClass: BILLING_CLASS,
    type: BENEFIT_TYPE,
  ) {
    let benefitData: Omit<Service, "condition" | "serviceLimit"> | string =
      getCopayCoinsurance(billingClass);
    // console.log("Benefit Data", benefitData);
    if (type === "COPAY") benefitData = benefitData?.copay;
    if (type === "COINSURANCE")
      benefitData = (benefitData as Omit<Service, "condition" | "serviceLimit">)
        ?.coinsurance;
    switch (benefitData) {
      case "CE001":
        return "No Charge";
        break;
      case "CE002":
        return "Not Covered";
        break;
      case "N/A":
        return benefitData;
        break;
      default:
        if (type === "COINSURANCE") return `${benefitData}%`;
        else return `$${benefitData}`;
    }
  }

  return (
    <div className="flex flex-col gap-5 rounded-md bg-slate-100 px-4 py-5">
      <div className="flex flex-col gap-3">
        <h6 className="font-bold">Total Estimated Cost:</h6>
        <div className="flex gap-1.5">
          <span className="font-semibold">Total Estimated Cost</span>
          <div className="flex flex-col items-start justify-start gap-2">
            <span> = Professional Fee + Institutional Fee</span>
            <span>
              {" "}
              {`= ${professionalRate === "N/A" ? "N/A" : professionalRate.includes("-") ? `[${professionalRate}]` : professionalRate} + ${institutionalRate === "N/A" ? "N/A" : institutionalRate.includes("-") ? `[${institutionalRate}]` : institutionalRate}`}
            </span>
            <span> {`= ${totalRate}`}</span>
          </div>
        </div>
      </div>

      <hr />

      <div className="flex flex-col gap-3">
        <h6 className="font-bold">Professional Fee:</h6>
        <div className="flex flex-col gap-2">
          <div className="flex justify-between gap-1.5">
            <span className="font-semibold">Contracted Rate</span>
            <span className="">
              {showContractedRateRange(BILLING_CLASS.PROFESSIONAL)}
            </span>
          </div>
          <div className="flex justify-between gap-1.5">
            <span className="font-semibold">Copay</span>
            <span className="">
              {showCopayCoinsuranceRange(
                BILLING_CLASS.PROFESSIONAL,
                BENEFIT_TYPE.COPAY,
              )}
            </span>
          </div>
          <div className="flex justify-between gap-1.5">
            <span className="font-semibold">Coinsurance</span>
            <span className="">
              {showCopayCoinsuranceRange(
                BILLING_CLASS.PROFESSIONAL,
                BENEFIT_TYPE.COINSURANCE,
              )}
            </span>
          </div>
          <div className="flex justify-between gap-1.5">
            <p>
              {" "}
              <span className="font-semibold">OOP Paid: </span>
              {showOopDed("OOP")}
            </p>
          </div>
          <div className="flex justify-between gap-1.5">
            <p>
              <span className="font-semibold">Deductible Paid: </span>
              {showOopDed("DED")}
            </p>
          </div>
          {(getCopayCoinsurance(BILLING_CLASS.PROFESSIONAL) as Service)
            ?.condition === "DED_WAIVED" && (
            <div className="flex justify-between gap-1.5">
              <span className="font-semibold">Deductible Waived</span>
              <span className="">Yes</span>
            </div>
          )}
          <div className="flex justify-between gap-1.5">
            <span className="font-semibold">
              Your Estimate (Professional):{" "}
            </span>
            <span className="">{professionalRate}</span>
          </div>
        </div>
      </div>

      <hr />

      <div className="flex flex-col gap-3">
        <h6 className="font-bold">Institutional Fee:</h6>
        <div className="flex flex-col gap-2">
          <div className="flex justify-between gap-1.5">
            <span className="font-semibold">Contracted Rate</span>
            <span className="">
              {showContractedRateRange(BILLING_CLASS.FACILITY)}
            </span>
          </div>
          <div className="flex justify-between gap-1.5">
            <span className="font-semibold">Copay</span>
            <span className="">
              {showCopayCoinsuranceRange(
                BILLING_CLASS.FACILITY,
                BENEFIT_TYPE.COPAY,
              )}
            </span>
          </div>
          <div className="flex justify-between gap-1.5">
            <span className="font-semibold">Coinsurance</span>
            <span className="">
              {showCopayCoinsuranceRange(
                BILLING_CLASS.FACILITY,
                BENEFIT_TYPE.COINSURANCE,
              )}
            </span>
          </div>
          <div className="flex justify-between gap-1.5">
            <p>
              <span className="font-semibold">OOP Paid: </span>
              {showOopDed("OOP")}
            </p>
          </div>
          <div className="flex justify-between gap-1.5">
            <p>
              <span className="font-semibold">Deductible Paid:</span>{" "}
              {showOopDed("DED")}
            </p>
          </div>
          {(getCopayCoinsurance(BILLING_CLASS.FACILITY) as Service)
            ?.condition === "DED_WAIVED" && (
            <div className="flex justify-between gap-1.5">
              <span className="font-semibold">Deductible Waived</span>
              <span className="">Yes</span>
            </div>
          )}
          <div className="flex justify-between gap-1.5">
            <span className="font-semibold">
              Your Estimate (Institutional):{" "}
            </span>
            <span className="">{institutionalRate}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PosLevelCostBreakdown;
